import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, HelpCircle, Book, Mail, MessageSquare, Video, FileText } from 'lucide-react';

const Support = () => {
  const navigate = useNavigate();

  const resources = [
    {
      icon: Book,
      title: 'User Manual',
      description: 'Complete guide to using all features of Hines Time',
      action: () => navigate('/user-manual'),
      actionText: 'View Manual'
    },
    {
      icon: Video,
      title: 'Video Tutorials',
      description: 'Watch step-by-step video guides and walkthroughs',
      action: () => navigate('/demo'),
      actionText: 'Watch Demo'
    },
    {
      icon: Mail,
      title: 'Email Support',
      description: 'Contact our support team for assistance',
      action: () => window.location.href = 'mailto:support@hinestime.com',
      actionText: 'Send Email'
    },
    {
      icon: MessageSquare,
      title: 'Contact Us',
      description: 'Fill out our contact form for general inquiries',
      action: () => navigate('/contact'),
      actionText: 'Contact Form'
    }
  ];

  const faqs = [
    {
      question: 'How do I add users to my account?',
      answer: 'Go to Settings → Manage Users → Click "Add User". Fill in their details and they\'ll receive an email and SMS invitation with temporary login credentials.'
    },
    {
      question: 'How does GPS location verification work?',
      answer: 'When workers clock in or start jobs, their device GPS location is captured. For jobs, we verify they are within 500 meters of the job site address. This ensures workers are on-site and prevents time theft.'
    },
    {
      question: 'Can I edit time entries if there are errors?',
      answer: 'Yes! Administrators can edit time entries in Reports → Time Editor. All edits are tracked with full audit trail (who edited, when, and why).'
    },
    {
      question: 'How does billing work?',
      answer: 'You get a 30-day free trial (credit card required). After the trial, you\'re charged a one-time $9.99 setup fee plus $5.99 per active user per month. The same numeric amount ($9.99 / $5.99) applies in USD, CAD, and EUR — only the currency symbol changes. Add or remove users anytime — billing adjusts automatically.'
    },
    {
      question: 'What happens if a worker forgets to clock out?',
      answer: 'The system automatically clocks them out after 8 hours for safety compliance. Administrators can also manually adjust time entries if needed.'
    },
    {
      question: 'Can I require photos for job completion?',
      answer: 'Yes! When creating a job, check "Photos Required". Workers must upload at least one photo before they can complete the job. Great for showing customers the finished work!'
    },
    {
      question: 'Is my data secure?',
      answer: 'Absolutely! We use bank-level encryption (TLS 1.2+, AES-256), two-factor authentication, and comply with GDPR. See our Security page for full details.'
    },
    {
      question: 'Can I cancel anytime?',
      answer: 'Yes! Cancel your subscription anytime. Cancellations take effect at the end of your current billing period. No long-term contracts or cancellation fees.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 pb-20">
      <div className="bg-gradient-to-r from-blue-600 to-green-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="w-6 h-6" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Support Center</h1>
              <p className="text-blue-100 mt-1">Find answers and get help</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Support Resources */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">How Can We Help?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {resources.map((resource, idx) => {
              const Icon = resource.icon;
              return (
                <Card key={idx} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={resource.action}>
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-green-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="font-bold text-gray-900 mb-2">{resource.title}</h3>
                    <p className="text-sm text-gray-600 mb-4">{resource.description}</p>
                    <Button variant="outline" size="sm" className="w-full">
                      {resource.actionText}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* FAQ Section */}
        <div>
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <Card key={idx}>
                <CardHeader className="bg-gray-50">
                  <CardTitle className="text-lg flex items-start">
                    <HelpCircle className="w-5 h-5 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                    <span>{faq.question}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <p className="text-gray-700">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Still Need Help */}
        <Card className="mt-12 bg-gradient-to-r from-blue-600 to-green-600 text-white">
          <CardContent className="p-8 text-center">
            <MessageSquare className="w-16 h-16 mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-3">Still Need Help?</h3>
            <p className="text-blue-100 mb-6">
              Our support team is ready to assist you. Reach out via email, phone, or our contact form.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/contact')}
                size="lg"
                className="bg-white text-blue-600 hover:bg-blue-50"
              >
                Contact Support
              </Button>
              <Button
                onClick={() => window.location.href = 'mailto:support@hinestime.com'}
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10"
              >
                Email Us
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Support;
